import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  isBookmarked?: boolean;
}

interface SearchHistoryContextType {
  searchHistory: SearchHistoryItem[];
  addToSearchHistory: (query: string) => void;
  removeFromSearchHistory: (timestamp: number) => void;
  clearSearchHistory: () => void;
  toggleBookmark: (timestamp: number) => void;
  bookmarks: SearchHistoryItem[];
}

const SearchHistoryContext = createContext<SearchHistoryContextType | undefined>(undefined);

/** Search history is kept in this browser, one list per signed-in account
 *  (SRS #41). A single shared key meant the next person to sign in on the same
 *  machine inherited the previous user's searches and bookmarks. */
export const storageKeyFor = (email: string | null | undefined): string =>
  email ? `searchHistory:${email.trim().toLowerCase()}` : 'searchHistory:anonymous';

const readHistory = (key: string): SearchHistoryItem[] => {
  try {
    const saved = localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Failed to parse search history', e);
    return [];
  }
};

export const SearchHistoryProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { user } = useAuth();
  const storageKey = storageKeyFor(user?.email);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>(() => readHistory(storageKey));
  const [loadedKey, setLoadedKey] = useState(storageKey);

  // The pre-#41 shared list is never shown to anyone again
  useEffect(() => { try { localStorage.removeItem('searchHistory'); } catch { /* storage blocked */ } }, []);

  // Switch lists when a different account signs in (or everyone signs out)
  useEffect(() => {
    if (loadedKey === storageKey) return;
    setSearchHistory(readHistory(storageKey));
    setLoadedKey(storageKey);
  }, [storageKey, loadedKey]);

  // Persist under the current account's key only
  useEffect(() => {
    if (loadedKey !== storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(searchHistory)); } catch { /* storage blocked */ }
  }, [searchHistory, storageKey, loadedKey]);

  const addToSearchHistory = useCallback((query: string) => {
    if (!query.trim()) return;
    
    setSearchHistory(prev => {
      // Remove if already exists to avoid duplicates
      const filtered = prev.filter(item => 
        item.query.toLowerCase() !== query.toLowerCase()
      );
      // Add to beginning of array (most recent first) and limit to 50 items
      return [{ query, timestamp: Date.now() }, ...filtered].slice(0, 50);
    });
  }, []);

  const removeFromSearchHistory = useCallback((timestamp: number) => {
    setSearchHistory(prev => prev.filter(item => item.timestamp !== timestamp));
  }, []);

  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
  }, []);

  const toggleBookmark = useCallback((timestamp: number) => {
    setSearchHistory(prev => 
      prev.map(item => 
        item.timestamp === timestamp 
          ? { ...item, isBookmarked: !item.isBookmarked }
          : item
      )
    );
  }, []);

  // Get bookmarked items
  const bookmarks = searchHistory.filter(item => item.isBookmarked);

  return (
    <SearchHistoryContext.Provider 
      value={{ 
        searchHistory, 
        addToSearchHistory, 
        removeFromSearchHistory, 
        clearSearchHistory,
        toggleBookmark,
        bookmarks
      }}
    >
      {children}
    </SearchHistoryContext.Provider>
  );
};

export const useSearchHistory = () => {
  const context = useContext(SearchHistoryContext);
  if (!context) {
    throw new Error('useSearchHistory must be used within a SearchHistoryProvider');
  }
  return context;
};
